<script setup lang="ts">
import { ref, computed } from 'vue'
import { SelectOption } from '@/munio/definitions'
import { trans } from '@/munio/i18n'
import Select from '@component/Select.vue'
import MdlTextfield from '@component/mdl/Textfield.vue'
import MdlButton from '@component/mdl/Button.vue'
import Icon from '@component/Icon.vue'

defineOptions({
  name: 'RadioFilter',
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    inputName: string
    disabled?: boolean
    value?: string | number
    options?: SelectOption[]
  }>(),
  {
    inputName: 'value',
    options: () => [],
  },
)

const emit = defineEmits(['activate'])

const search = ref(null)
const compiledOptions = ref(props.options.map(optionNormalizer))
const enableSearch = computed(
  () => props.options.some((option) => option.children?.length > 0) || props.options.length > 5,
)

function optionNormalizer(option: SelectOption) {
  const { children, ...opt } = option

  const structure = {
    id: opt.value,
    label: trans(opt.label),
    isDisabled: opt.disabled,
    ...opt,
    children: children && children?.length > 0 ? children.map(optionNormalizer) : undefined,
  }

  return structure
}
</script>

<template>
  <div class="mdl-filter__form" :class="$attrs.class" @click="emit('activate', $event)" @click.stop>
    <MdlTextfield v-if="enableSearch" v-model="search" class="px-2" :placeholder="trans('Search')">
      <div class="px-1">
        <MdlButton v-if="search" icon="cancel" dense small class="opaque" @click="search = null" />
      </div>
    </MdlTextfield>
    <Select
      :name="inputName"
      :model-value="value"
      :options="compiledOptions"
      :search-query="search"
      :default-expand-level="2"
      override-is-disabled
      display-only
    >
      <template #option-label="{ node, labelClassName }">
        <label :class="labelClassName" class="gap-x-2">
          <img v-if="node.raw.image" :src="node.raw.image" class="avatar" :alt="trans('Avatar')" />
          <span class="flex flex-col">
            <small v-if="node.raw.identifier" class="opaque">{{ node.raw.identifier }}</small>
            <span>
              <Icon v-if="node.raw.icon" :name="node.raw.icon" small />
              {{ node.label }}
            </span>
            <span v-if="node.raw.description" class="opaque text-sm">
              {{ node.raw.description }}
            </span>
          </span>
        </label>
      </template>
    </Select>
  </div>
</template>
